
import { onMounted, reactive, ref, nextTick, onUnmounted } from "vue";
import { DownOutlined, UpOutlined } from "@ant-design/icons-vue";
import XAtable from "@/components/XATable.vue";
import {
  sysDataQueryList,
  sysDataSave,
  sysDataDel,
  userQueryPageList,
  sysDataStateXls,
  commonUpload,
  sysDataStateNotUseExpend,
  sysDataStateNotUseNotExpend,
  sysDataStateStop,
  sysDataStateUse,
  sysDataQueryFingerIdList,
  sysDataGetAccountStopXls,
  sysUserAdsQueryList,
} from "@/api/api";
import XDialog from "@/components/XDialog.vue";
import { FormInstance, message } from "ant-design-vue";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import * as echarts from "echarts";
import * as XLSX from "xlsx";

interface SearchFormState {
  adsId: string;
  createBy?: string | number;
  userId: string;
  status: string;
  isShow: string | number;
  online: string | number;
  pageNo: number;
  pageSize: number;
}

interface FormState {
  id: string | null;
  adsId: string;
  userId: string | number;
  price: number;
  intro: string;
  tag: string;
  amount: number;
  beginTime: Dayjs | null;
  endTime: Dayjs | null;
  bmBase: string;
  bmLevel: string;
  gmt: string;
  fingerId: string;
  remark: string;
  status: number;
  stopType: number;
  cardNo: string;
  cardChannel: number;
  online: number;
  isShow: number;
}

export default {
  name: "AddataManage",
  components: {
    XAtable,
    XDialog,
    DownOutlined,
    UpOutlined,
  },
  setup() {
    const searchFormState = reactive<SearchFormState>({
      adsId: "",
      createBy: undefined,
      userId: "",
      status: "",
      isShow: "",
      online: "",
      pageNo: 1,
      pageSize: 10,
    });

    const formState = reactive<FormState>({
      id: null,
      adsId: "",
      userId: "",
      price: 0,
      intro: "",
      tag: "",
      amount: 0,
      beginTime: null,
      endTime: null,
      bmBase: "",
      bmLevel: "",
      gmt: "",
      fingerId: "",
      remark: "",
      status: 0,
      stopType: 0,
      cardNo: "",
      cardChannel: 0,
      online: 0,
      isShow: 0,
    });

    const tagArray = ref<string[]>([]);

    const parseTagToArray = (tag: string | null | undefined): string[] => {
      if (!tag) return [];
      return String(tag)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    };

    const formatPrice = (price: number | string | null | undefined) => {
      if (price === null || price === undefined || price === "") return "-";
      const num = Number(price);
      if (Number.isNaN(num)) return "-";
      return num.toFixed(2);
    };

    const isAdd = ref(true);
    const dislogVisible = ref(false);
    const addLoading = ref(false);
    const pageNo = ref(1);
    const total = ref(0);
    const loading = ref(false);
    const userList = ref([]);
    const bmLevelOptions = ref<string[]>([]);
    const batchDisableXlsLoading = ref(false);
    const batchAddLoading = ref(false);
    const exportStopAccountLoading = ref(false);

    // 图表显示控制
    const chartsVisible = ref(false);
    const toggleCharts = () => {
      chartsVisible.value = !chartsVisible.value;
      if (chartsVisible.value) {
        // 展开时，等待DOM更新后调整图表大小
        nextTick(() => {
          setTimeout(() => {
            notUseExpendChart?.resize();
            notUseNotExpendChart?.resize();
            stopChart?.resize();
            useChart?.resize();
          }, 100);
        });
      }
    };

    // 图表相关
    const notUseExpendChartRef = ref<HTMLElement | null>(null);
    const notUseNotExpendChartRef = ref<HTMLElement | null>(null);
    const stopChartRef = ref<HTMLElement | null>(null);
    const useChartRef = ref<HTMLElement | null>(null);
    let notUseExpendChart: echarts.ECharts | null = null;
    let notUseNotExpendChart: echarts.ECharts | null = null;
    let stopChart: echarts.ECharts | null = null;
    let useChart: echarts.ECharts | null = null;

    // 图表数据
    const notUseExpendData = ref<any>({});
    const notUseNotExpendData = ref<any>({});
    const stopData = ref<any[]>([]);
    const useData = ref<any>({});

    // 时区列表（完整版：27个整点时区 + 5个半小时区 + 3个45分钟区 = 35个）
    const timeZoneList = [
      // 西时区（GMT-12 到 GMT-1）
      { value: "GMT-12", label: "GMT-12 (IDLW) - 国际日期变更线西侧时间" },
      { value: "GMT-11", label: "GMT-11 (NUT) - 纽埃时间" },
      { value: "GMT-10", label: "GMT-10 (HST) - 夏威夷标准时间" },
      { value: "GMT-9:30", label: "GMT-9:30 (MART) - 马克萨斯群岛时间" },
      { value: "GMT-9", label: "GMT-9 (AKST) - 阿拉斯加标准时间" },
      { value: "GMT-8", label: "GMT-8 (PST) - 美国太平洋标准时间" },
      { value: "GMT-7", label: "GMT-7 (MST) - 美国山地标准时间" },
      { value: "GMT-6", label: "GMT-6 (CST) - 美国中部标准时间" },
      { value: "GMT-5", label: "GMT-5 (EST) - 美国东部标准时间" },
      { value: "GMT-4", label: "GMT-4 (AST) - 大西洋标准时间" },
      { value: "GMT-3:30", label: "GMT-3:30 (NST) - 纽芬兰标准时间" },
      { value: "GMT-3", label: "GMT-3 (ART) - 阿根廷时间" },
      { value: "GMT-2", label: "GMT-2 (FNT) - 费尔南多·迪诺罗尼亚时间" },
      { value: "GMT-1", label: "GMT-1 (AZOT) - 亚速尔群岛时间" },

      // 零时区
      { value: "GMT+0", label: "GMT+0 (UTC) - 协调世界时" },

      // 东时区（GMT+1 到 GMT+14）
      { value: "GMT+1", label: "GMT+1 (CET) - 中欧时间" },
      { value: "GMT+2", label: "GMT+2 (EET) - 东欧时间" },
      { value: "GMT+3", label: "GMT+3 (MSK) - 莫斯科时间" },
      { value: "GMT+3:30", label: "GMT+3:30 (IRST) - 伊朗标准时间" },
      { value: "GMT+4", label: "GMT+4 (GST) - 海湾标准时间" },
      { value: "GMT+4:30", label: "GMT+4:30 (AFT) - 阿富汗时间" },
      { value: "GMT+5", label: "GMT+5 (PKT) - 巴基斯坦标准时间" },
      { value: "GMT+5:45", label: "GMT+5:45 (NPT) - 尼泊尔时间" },
      { value: "GMT+5:30", label: "GMT+5:30 (IST) - 印度标准时间" },
      { value: "GMT+6", label: "GMT+6 (BST) - 孟加拉标准时间" },
      { value: "GMT+6:30", label: "GMT+6:30 (MMT) - 缅甸时间" },
      { value: "GMT+7", label: "GMT+7 (ICT) - 印度支那时间" },
      { value: "GMT+8", label: "GMT+8 (CST) - 中国标准时间" },
      { value: "GMT+8:45", label: "GMT+8:45 (ACWST) - 澳大利亚中西部标准时间" },
      { value: "GMT+9", label: "GMT+9 (JST) - 日本标准时间" },
      { value: "GMT+9:30", label: "GMT+9:30 (ACST) - 澳大利亚中部标准时间" },
      { value: "GMT+10", label: "GMT+10 (AEST) - 澳大利亚东部标准时间" },
      { value: "GMT+10:30", label: "GMT+10:30 (LHST) - 豪勋爵岛标准时间" },
      { value: "GMT+11", label: "GMT+11 (VUT) - 瓦努阿图时间" },
      { value: "GMT+12", label: "GMT+12 (NZST) - 新西兰标准时间" },
      { value: "GMT+12:45", label: "GMT+12:45 (CHAST) - 查塔姆群岛标准时间" },
      { value: "GMT+13", label: "GMT+13 (TOT) - 汤加时间" },
      { value: "GMT+14", label: "GMT+14 (LINT) - 莱恩群岛时间" },
    ];

    const columns = reactive([
      {
        title: "ID",
        key: "id",
        dataIndex: "id",
        width: 80,
      },
      {
        title: "指纹编号",
        key: "fingerId",
        dataIndex: "fingerId",
        width: 120,
      },
      {
        title: "时区",
        key: "gmt",
        dataIndex: "gmt",
        width: 120,
      },
      {
        title: "母BMID",
        key: "bmBase",
        dataIndex: "bmBase",
        width: 170,
      },
      {
        title: "广告账户ID",
        key: "adsId",
        dataIndex: "adsId",
        width: 180,
      },
      {
        title: "BM情况",
        key: "bmLevel",
        dataIndex: "bmLevel",
        width: 120,
      },
      {
        title: "额度",
        key: "amount",
        dataIndex: "amount",
        width: 100,
      },
      {
        title: "FB连接",
        key: "online",
        dataIndex: "online",
        width: 150,
        slots: {
          customRender: "online",
        },
      },
      {
        title: "状态",
        key: "status",
        dataIndex: "status",
        width: 100,
        slots: {
          customRender: "status",
        },
      },
      {
        title: "用户",
        key: "userName",
        dataIndex: "userName",
        width: 200,
        slots: {
          customRender: "userName",
        },
      },
      {
        title: "上架",
        key: "isShow",
        dataIndex: "isShow",
        width: 80,
        slots: {
          customRender: "isShow",
        },
      },
      {
        title: "售价($)",
        key: "price",
        dataIndex: "price",
        width: 100,
        slots: {
          customRender: "price",
        },
      },
      {
        title: "简介",
        key: "intro",
        dataIndex: "intro",
        width: 200,
        slots: {
          customRender: "intro",
        },
      },
      {
        title: "标签",
        key: "tag",
        dataIndex: "tag",
        width: 160,
        slots: {
          customRender: "tag",
        },
      },
      {
        title: "创建者(户)",
        key: "createBy",
        dataIndex: "createBy",
        width: 120,
        slots: {
          customRender: "createBy",
        },
      },
      {
        title: "创建时间",
        key: "createTime",
        dataIndex: "createTime",
        width: 180,
      },
      {
        title: "操作",
        key: "control",
        dataIndex: "control",
        width: 150,
        fixed: "right",
        slots: {
          customRender: "control",
        },
      },
    ]);

    const tableData = ref([]);
    const formRef = ref<FormInstance>();
    const searchFormRef = ref<FormInstance>();

    interface CreatorOption {
      id: string | number;
      name: string;
    }

    const CREATOR_FILTER_PAGE_SIZE = 50;
    const creatorFilterList = ref<CreatorOption[]>([]);
    const creatorFilterLoading = ref(false);
    const creatorFilterPageNo = ref(1);
    const creatorFilterTotal = ref(0);
    const creatorFilterKeyword = ref("");

    const getCreatorFilterList = (reset = false) => {
      if (reset) {
        creatorFilterPageNo.value = 1;
        creatorFilterList.value = [];
      }

      creatorFilterLoading.value = true;
      const isNumeric = /^\d+$/.test(creatorFilterKeyword.value);
      const params: Record<string, string | number> = {
        pageNo: creatorFilterPageNo.value,
        pageSize: CREATOR_FILTER_PAGE_SIZE,
      };
      if (creatorFilterKeyword.value) {
        if (isNumeric) {
          params.id = creatorFilterKeyword.value;
        } else {
          params.name = creatorFilterKeyword.value;
        }
      }

      sysUserAdsQueryList(params)
        .then((res) => {
          creatorFilterLoading.value = false;
          if (res.code === "200" && res.data) {
            creatorFilterTotal.value = res.data.total ?? 0;
            const raw = res.data.records || [];
            const records: CreatorOption[] = raw.map((row: Record<string, unknown>) => ({
              id: (row.id ?? row.userId ?? row.createBy) as string | number,
              name: String(row.name ?? row.userName ?? row.createByName ?? ""),
            }));
            if (reset) {
              creatorFilterList.value = records;
            } else {
              creatorFilterList.value = [...creatorFilterList.value, ...records];
            }
          }
        })
        .catch(() => {
          creatorFilterLoading.value = false;
        });
    };

    const handleCreatorFilterSearch = (value: string) => {
      creatorFilterKeyword.value = value || "";
      getCreatorFilterList(true);
    };

    const handleCreatorFilterScroll = (e: any) => {
      const { target } = e;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
        if (
          creatorFilterList.value.length < creatorFilterTotal.value &&
          !creatorFilterLoading.value
        ) {
          creatorFilterPageNo.value += 1;
          getCreatorFilterList(false);
        }
      }
    };

    const handleCreatorFilterFocus = () => {
      if (creatorFilterList.value.length === 0) {
        creatorFilterKeyword.value = "";
        getCreatorFilterList(true);
      }
    };

    const handleCreatorFilterClear = () => {
      creatorFilterKeyword.value = "";
      getCreatorFilterList(true);
    };

    const search = () => {
      searchFormState.pageNo = 1;
      getData();
    };

    const resetForm = () => {
      searchFormState.pageNo = 1;
      searchFormRef.value.resetFields();
      creatorFilterKeyword.value = "";
      creatorFilterPageNo.value = 1;
      creatorFilterList.value = [];
      creatorFilterTotal.value = 0;
      getData();
    };

    const getData = () => {
      loading.value = true;
      sysDataQueryList(searchFormState).then((res) => {
        loading.value = false;
        if (res.code === "200") {
          total.value = res.data.total;
          tableData.value = res.data.records;
        }
      });
    };

    const pageNoChange = (val: number) => {
      searchFormState.pageNo = val;
      getData();
    };

    const onShowSizeChange = (current: number, size: number) => {
      searchFormState.pageSize = size;
      getData();
    };

    const addClick = () => {
      isAdd.value = true;
      dislogVisible.value = true;
      formState.id = null;
      formState.adsId = "";
      formState.userId = "";
      formState.price = 0;
      formState.intro = "";
      formState.tag = "";
      formState.amount = 0;
      formState.beginTime = null;
      formState.endTime = null;
      formState.bmBase = "";
      formState.bmLevel = "";
      formState.gmt = "";
      formState.fingerId = "";
      formState.remark = "";
      formState.status = 0;
      formState.stopType = 0;
      formState.cardNo = "";
      formState.cardChannel = 0;
      formState.online = 0;
      formState.isShow = 0;
      tagArray.value = [];
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editClick = (row: any) => {
      isAdd.value = false;
      formState.id = row.id;
      formState.adsId = row.adsId;
      formState.userId = row.userId;
      formState.price = row.price ?? 0;
      formState.intro = row.intro || "";
      formState.tag = row.tag || "";
      tagArray.value = parseTagToArray(row.tag);
      formState.amount = row.amount;
      formState.beginTime = row.beginTime ? dayjs(row.beginTime) : null;
      formState.endTime = row.endTime ? dayjs(row.endTime) : null;
      formState.bmBase = row.bmBase;
      formState.bmLevel = row.bmLevel;
      formState.gmt = row.gmt || "";
      formState.fingerId = row.fingerId;
      formState.remark = row.remark;
      formState.status = row.status ?? 0;
      formState.stopType = row.stopType !== undefined ? row.stopType : 0;
      formState.cardNo = row.cardNo || "";
      formState.cardChannel = row.cardChannel !== undefined ? row.cardChannel : 0;
      formState.online = row.online !== undefined ? row.online : 0;
      formState.isShow =
        row.isShow !== undefined && row.isShow !== null ? Number(row.isShow) : 0;
      dislogVisible.value = true;
    };

    const confirm = async () => {
      try {
        await formRef.value.validateFields();
        addLoading.value = true;

        const submitData = {
          ...formState,
          tag: tagArray.value.map((item) => item.trim()).filter(Boolean).join(","),
          beginTime: formState.beginTime
            ? formState.beginTime.format("YYYY-MM-DD HH:mm:ss")
            : null,
          endTime: formState.endTime
            ? formState.endTime.format("YYYY-MM-DD HH:mm:ss")
            : null,
        };

        sysDataSave(submitData).then((res) => {
          addLoading.value = false;
          if (res.code === "200") {
            message.success(isAdd.value ? "添加成功！" : "编辑成功！");
            dislogVisible.value = false;
            getData();
          } else {
            // message.error(res.message || "操作失败！");
          }
        });
      } catch (errorInfo) {
        console.log("Failed:", errorInfo);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deleteClick = (row: any) => {
      sysDataDel({ id: row.id }).then((res) => {
        if (res.code === "200") {
          message.success("删除成功！");
          getData();
        } else {
          //   message.error(res.message || "删除失败！");
        }
      });
    };

    const getUserListData = () => {
      userQueryPageList({}).then((res) => {
        if (res.code === "200") {
          userList.value = res.data.records;
        }
      });
    };

    // 获取 BM情况 下拉选项
    const getBmLevelOptions = () => {
      sysDataQueryFingerIdList({})
        .then((res) => {
          if (res.code === "200" && res.data) {
            const list = Array.isArray(res.data) ? res.data : res.data.records || [];
            bmLevelOptions.value = list
              .map((item: any) => item.fingerId)
              .filter(
                (v: any, index: number, arr: any[]) =>
                  v && arr.indexOf(v) === index
              );
          }
        })
        .catch(() => undefined);
    };

    // 打开 Facebook 广告账户「管理/财务」页（新标签页）
    const openFbAdAccountManage = (row: any) => {
      const businessId = row?.bmBase || row?.bmName || "";
      const assetId = row?.adsId || "";
      if (!businessId || !assetId) return;

      const url = `https://business.facebook.com/latest/settings/ad_accounts/?business_id=${encodeURIComponent(
        businessId
      )}&selected_asset_id=${encodeURIComponent(
        assetId
      )}&selected_asset_type=ad-account`;
      window.open(url, "_blank", "noopener,noreferrer");
    };

    const openFbAdAccountFinance = (row: any) => {
      const businessId = row?.bmBase || row?.bmName || "";
      const assetId = row?.adsId || "";
      if (!businessId || !assetId) return;

      const url = `https://business.facebook.com/latest/billing_hub/accounts/?business_id=${encodeURIComponent(
        businessId
      )}&asset_id=${encodeURIComponent(assetId)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    };

    // 处理时区格式转换：+5 -> GMT+5, +12 -> GMT+12
    const formatTimeZone = (gmt: string | number | null | undefined): string => {
      if (!gmt && gmt !== 0) return "";
      const gmtStr = String(gmt).trim();
      // 如果已经是 GMT+ 格式，直接返回
      if (gmtStr.toUpperCase().startsWith("GMT")) {
        return gmtStr;
      }
      // 处理 +5, +12 等格式
      if (gmtStr.startsWith("+")) {
        return `GMT${gmtStr}`;
      }
      // 处理 -5, -12 等格式
      if (gmtStr.startsWith("-")) {
        return `GMT${gmtStr}`;
      }
      // 处理纯数字，假设为正数
      const num = Number(gmtStr);
      if (!isNaN(num)) {
        return num >= 0 ? `GMT+${num}` : `GMT${num}`;
      }
      return gmtStr;
    };

    // 处理批量添加XLS文件上传
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleBatchAddUpload = (file: any) => {
      // 检查文件类型
      const isXls =
        file.type === "application/vnd.ms-excel" ||
        file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".xlsx");

      if (!isXls) {
        message.error("只能上传 .xls 或 .xlsx 格式的文件！");
        return false;
      }

      batchAddLoading.value = true;

      // 读取文件
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          // 获取第一个工作表
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // 将工作表转换为 JSON 数组
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });

          if (jsonData.length < 2) {
            message.error("Excel 文件至少需要包含表头和数据行！");
            batchAddLoading.value = false;
            return;
          }

          // 跳过表头，从第二行开始处理数据
          const dataRows = jsonData.slice(1) as any[][];

          // 验证并转换数据
          const validData: any[] = [];
          const errors: string[] = [];

          dataRows.forEach((row: any[], index: number) => {
            const rowNum = index + 2; // 实际行号（包含表头）

            // 跳过空行
            if (row.every((cell) => !cell || String(cell).trim() === "")) {
              return;
            }

            // 列顺序：广告账户ID, 额度, 母BM, BM情况, 时区, 指纹
            const adsId = row[0] ? String(row[0]).trim() : "";
            const amount = row[1] !== undefined && row[1] !== null && row[1] !== "" ? Number(row[1]) : 0;
            const bmBase = row[2] ? String(row[2]).trim() : "";
            const bmLevel = row[3] ? String(row[3]).trim() : "";
            const gmt = row[4] ? formatTimeZone(row[4]) : "";
            const fingerId = row[5] ? String(row[5]).trim() : "";

            // 验证必填字段
            if (!adsId) {
              errors.push(`第 ${rowNum} 行：广告账户ID不能为空`);
              return;
            }
            if (isNaN(amount) || amount <= 0) {
              errors.push(`第 ${rowNum} 行：额度必须大于 0`);
              return;
            }

            validData.push({
              adsId,
              cardNo: "",
              cardChannel: 0,
              amount,
              bmBase,
              bmLevel,
              gmt,
              fingerId,
              status: 0, // 默认状态为待出售
            });
          });

          if (errors.length > 0) {
            message.error(`数据验证失败：\n${errors.slice(0, 5).join("\n")}${errors.length > 5 ? `\n...还有 ${errors.length - 5} 个错误` : ""}`);
            batchAddLoading.value = false;
            return;
          }

          if (validData.length === 0) {
            message.error("没有有效的数据行！");
            batchAddLoading.value = false;
            return;
          }

          // 批量添加数据
          let successCount = 0;
          let failCount = 0;

          // 使用 Promise.all 并发上传，但限制并发数量
          const batchSize = 5; // 每批处理5条
          const batches: any[][] = [];
          for (let i = 0; i < validData.length; i += batchSize) {
            batches.push(validData.slice(i, i + batchSize));
          }

          const processBatch = async (batch: any[]) => {
            const promises = batch.map((item) => {
              return sysDataSave(item)
                .then((res) => {
                  if (res.code === "200") {
                    successCount++;
                    return { success: true, data: item };
                  } else {
                    failCount++;
                    return { success: false, data: item, error: res.message || "添加失败" };
                  }
                })
                .catch((error) => {
                  failCount++;
                  return { success: false, data: item, error: error.message || "添加失败" };
                });
            });

            return Promise.all(promises);
          };

          // 顺序处理每个批次
          const processAllBatches = async () => {
            for (let i = 0; i < batches.length; i++) {
              await processBatch(batches[i]);
            }
          };

          processAllBatches()
            .then(() => {
              batchAddLoading.value = false;
              if (failCount === 0) {
                message.success(`批量添加成功！共 ${successCount} 条数据`);
              } else {
                message.warning(`批量添加完成！成功 ${successCount} 条，失败 ${failCount} 条`);
              }
              // 刷新列表数据
              getData();
            })
            .catch((error) => {
              batchAddLoading.value = false;
              console.error("批量添加失败:", error);
              message.error("批量添加过程中发生错误！");
            });
        } catch (error) {
          batchAddLoading.value = false;
          console.error("解析 Excel 文件失败:", error);
          message.error("解析 Excel 文件失败，请检查文件格式！");
        }
      };

      reader.onerror = () => {
        batchAddLoading.value = false;
        message.error("读取文件失败！");
      };

      reader.readAsArrayBuffer(file);
      return false; // 阻止自动上传
    };

    // 处理批量停用XLS文件上传
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleBatchDisableXlsUpload = (file: any) => {
      // 检查文件类型
      const isXls =
        file.type === "application/vnd.ms-excel" ||
        file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".xlsx");

      if (!isXls) {
        message.error("只能上传 .xls 或 .xlsx 格式的文件！");
        return false;
      }

      const formData = new FormData();
      formData.append("file", file);

      batchDisableXlsLoading.value = true;
      commonUpload(formData)
        .then((res) => {
          if (res.code === "200" && res.data) {
            const fileUrl = res.data;
            // 上传成功后，调用批量停用xls接口
            return sysDataStateXls({ url: fileUrl });
          } else {
            throw new Error(res.message || "上传失败！");
          }
        })
        .then((res) => {
          batchDisableXlsLoading.value = false;
          if (res.code === "200") {
            message.success("上传并批量停用成功！");
            // 刷新列表数据
            getData();
          } else {
            // message.error(res.message || "批量停用失败！");
          }
        })
        .catch((error) => {
          batchDisableXlsLoading.value = false;
          console.error("上传或批量停用失败:", error);
          // message.error(error.message || "上传或批量停用失败！");
        });

      return false; // 阻止自动上传
    };

    const exportStopAccountClick = () => {
      exportStopAccountLoading.value = true;
      sysDataGetAccountStopXls()
        .then((res: unknown) => {
          exportStopAccountLoading.value = false;
          if (res instanceof Blob) {
            const url = window.URL.createObjectURL(res);
            const link = document.createElement("a");
            link.href = url;
            link.download = `禁用账户_${dayjs().format("YYYY-MM-DD_HHmm")}.xls`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            message.success("导出成功！");
          } else {
            message.error("导出失败，请重试！");
          }
        })
        .catch((error) => {
          exportStopAccountLoading.value = false;
          console.error("导出禁用账户失败:", error);
          message.error("导出失败，请重试！");
        });
    };

    // 初始化图表
    const initCharts = () => {
      if (notUseExpendChartRef.value) {
        notUseExpendChart = echarts.init(notUseExpendChartRef.value);
      }
      if (notUseNotExpendChartRef.value) {
        notUseNotExpendChart = echarts.init(notUseNotExpendChartRef.value);
      }
      if (stopChartRef.value) {
        stopChart = echarts.init(stopChartRef.value);
      }
      if (useChartRef.value) {
        useChart = echarts.init(useChartRef.value);
      }
    };

    // 更新未分配-消耗图表
    const updateNotUseExpendChart = () => {
      if (!notUseExpendChart) return;
      const data = notUseExpendData.value || {};
      const gmtList: string[] = [];
      const externalCounts: number[] = [];
      const internalCounts: number[] = [];

      Object.keys(data).forEach((gmt) => {
        const item = data[gmt];
        if (item) {
          const gmtValue = item.gmt !== undefined ? item.gmt : (gmt || "");
          gmtList.push(gmtValue);
          externalCounts.push(item.externalCount || 0);
          internalCounts.push(item.internalCount || 0);
        }
      });

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        legend: {
          data: ["外部", "内部"],
          top: 10,
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: gmtList,
          axisLabel: {
            rotate: gmtList.length > 10 ? 45 : 0,
            fontSize: 11,
          },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLabel: {
            formatter: (value: number) => {
              const intValue = Math.floor(value);
              return intValue.toString();
            },
          },
        },
        series: [
          {
            name: "外部",
            type: "bar",
            data: externalCounts,
            itemStyle: { color: "#1890ff" },
          },
          {
            name: "内部",
            type: "bar",
            data: internalCounts,
            itemStyle: { color: "#52c41a" },
          },
        ],
      };
      notUseExpendChart.setOption(option);
    };

    // 更新未分配-未消耗图表
    const updateNotUseNotExpendChart = () => {
      if (!notUseNotExpendChart) return;
      const data = notUseNotExpendData.value || {};
      const gmtList: string[] = [];
      const notUsedCounts: number[] = [];

      Object.keys(data).forEach((gmt) => {
        const item = data[gmt];
        if (item) {
          const gmtValue = item.gmt !== undefined ? item.gmt : (gmt || "");
          gmtList.push(gmtValue);
          notUsedCounts.push(item.notUsedCount || 0);
        }
      });

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: gmtList,
          axisLabel: {
            rotate: gmtList.length > 10 ? 45 : 0,
            fontSize: 11,
          },
        },
        yAxis: {
          type: "value",
        },
        series: [
          {
            type: "bar",
            data: notUsedCounts,
            itemStyle: { color: "#faad14" },
          },
        ],
      };
      notUseNotExpendChart.setOption(option);
    };

    // 更新被封账户图表
    const updateStopChart = () => {
      if (!stopChart) return;
      const data = stopData.value || [];
      const gmtList: string[] = [];
      const totals: number[] = [];

      data.forEach((item: any) => {
        gmtList.push(item.gmt !== undefined ? item.gmt : "");
        totals.push(item.total || 0);
      });

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: gmtList,
          axisLabel: {
            rotate: gmtList.length > 10 ? 45 : 0,
            fontSize: 11,
          },
        },
        yAxis: {
          type: "value",
        },
        series: [
          {
            type: "bar",
            data: totals,
            itemStyle: { color: "#ff4d4f" },
          },
        ],
      };
      stopChart.setOption(option);
    };

    // 更新使用中图表
    const updateUseChart = () => {
      if (!useChart) return;
      const data = useData.value || {};
      const gmtList: string[] = [];
      const externalCounts: number[] = [];
      const internalCounts: number[] = [];

      Object.keys(data).forEach((gmt) => {
        const item = data[gmt];
        if (item) {
          const gmtValue = item.gmt !== undefined ? item.gmt : (gmt || "");
          gmtList.push(gmtValue);
          externalCounts.push(item.externalCount || 0);
          internalCounts.push(item.internalCount || 0);
        }
      });

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        legend: {
          data: ["外部", "内部"],
          top: 10,
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: gmtList,
          axisLabel: {
            rotate: gmtList.length > 10 ? 45 : 0,
            fontSize: 11,
          },
        },
        yAxis: {
          type: "value",
        },
        series: [
          {
            name: "外部",
            type: "bar",
            barGap: 0,
            data: externalCounts,
            itemStyle: { color: "#1890ff" },
          },
          {
            name: "内部",
            type: "bar",
            data: internalCounts,
            itemStyle: { color: "#52c41a" },
          },
        ],
      };
      useChart.setOption(option);
    };

    onMounted(async () => {
      getUserListData();
      getBmLevelOptions();
      getData();

      // 初始化图表
      await nextTick();
      initCharts();

      // 调用四个图表接口并更新图表
      console.log("========== 开始调用图表接口 ==========");

      // 1. 图表-未分配-消耗
      sysDataStateNotUseExpend({})
        .then((res) => {
          console.log("1. 图表-未分配-消耗 (/sysData/state/notUse/expend):", res);
          if (res.code === "200" && res.data) {
            notUseExpendData.value = res.data;
            updateNotUseExpendChart();
          }
        })
        .catch((error) => {
          console.error("1. 图表-未分配-消耗 接口错误:", error);
        });

      // 2. 图表-未分配-未消耗
      sysDataStateNotUseNotExpend({})
        .then((res) => {
          console.log("2. 图表-未分配-未消耗 (/sysData/state/notUse/notExpend):", res);
          if (res.code === "200" && res.data) {
            notUseNotExpendData.value = res.data;
            updateNotUseNotExpendChart();
          }
        })
        .catch((error) => {
          console.error("2. 图表-未分配-未消耗 接口错误:", error);
        });

      // 3. 图表-被封账户
      sysDataStateStop({})
        .then((res) => {
          console.log("3. 图表-被封账户 (/sysData/state/stop):", res);
          if (res.code === "200" && res.data) {
            stopData.value = Array.isArray(res.data) ? res.data : [];
            updateStopChart();
          }
        })
        .catch((error) => {
          console.error("3. 图表-被封账户 接口错误:", error);
        });

      // 4. 图表-使用中
      sysDataStateUse({})
        .then((res) => {
          console.log("4. 图表-使用中 (/sysData/state/use):", res);
          if (res.code === "200" && res.data) {
            useData.value = res.data;
            updateUseChart();
          }
        })
        .catch((error) => {
          console.error("4. 图表-使用中 接口错误:", error);
        });

      console.log("========== 图表接口调用完成 ==========");
    });

    onUnmounted(() => {
      notUseExpendChart?.dispose();
      notUseNotExpendChart?.dispose();
      stopChart?.dispose();
      useChart?.dispose();
    });

    return {
      isAdd,
      formRef,
      dislogVisible,
      addClick,
      confirm,
      searchFormState,
      searchFormRef,
      formState,
      tagArray,
      parseTagToArray,
      formatPrice,
      addLoading,
      columns,
      tableData,
      pageNo,
      total,
      loading,
      search,
      resetForm,
      pageNoChange,
      onShowSizeChange,
      editClick,
      deleteClick,
      userList,
      timeZoneList,
      handleBatchDisableXlsUpload,
      batchDisableXlsLoading,
      exportStopAccountLoading,
      exportStopAccountClick,
      handleBatchAddUpload,
      batchAddLoading,
      notUseExpendChartRef,
      notUseNotExpendChartRef,
      stopChartRef,
      useChartRef,
      chartsVisible,
      toggleCharts,
      bmLevelOptions,
      openFbAdAccountManage,
      openFbAdAccountFinance,
      creatorFilterList,
      creatorFilterLoading,
      handleCreatorFilterSearch,
      handleCreatorFilterScroll,
      handleCreatorFilterFocus,
      handleCreatorFilterClear,
    };
  },
};
